import React, { useEffect, useState } from 'react';
import { Clock, ExternalLink, Zap, Flame } from 'lucide-react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATO_COLOR = {
  bozza: 'bg-slate-100 text-slate-700',
  documenti_richiesti: 'bg-amber-100 text-amber-700',
  documenti_ricevuti: 'bg-sky-100 text-sky-700',
  in_lavorazione: 'bg-amber-100 text-amber-700',
  inviata: 'bg-blue-100 text-blue-700',
  inserita_portale: 'bg-indigo-100 text-indigo-700',
  accettata: 'bg-emerald-100 text-emerald-700',
  attiva: 'bg-emerald-100 text-emerald-800',
  pagabile: 'bg-emerald-100 text-emerald-800',
  chiuso: 'bg-slate-200 text-slate-700 line-through',
  scartata: 'bg-red-100 text-red-700',
  annullata: 'bg-red-100 text-red-700',
  rifiutata: 'bg-red-100 text-red-700',
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

/**
 * Timeline dei contratti sullo stesso POD (o PDR) intestati allo stesso codice fiscale.
 * Consente di vedere subito la storia della fornitura: switch, rinnovi, subentri.
 */
const PodTimelineCard = ({ codice, codiceFiscale, tipo = 'pod', currentContractId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!codice || !codiceFiscale) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/pod-history`, {
      params: { codice, codice_fiscale: codiceFiscale },
    }).then(res => {
      if (!cancelled) setData(res.data);
    }).catch(() => {
      if (!cancelled) setData(null);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [codice, codiceFiscale]);

  if (!codice || !codiceFiscale) return null;

  const Icon = tipo === 'pdr' ? Flame : Zap;
  const label = tipo === 'pdr' ? 'PDR' : 'POD';
  const contracts = data?.contracts || [];

  return (
    <Card className="border-slate-200" data-testid="pod-timeline-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className={`w-4 h-4 ${tipo === 'pdr' ? 'text-sky-500' : 'text-amber-500'}`} />
          Storia {label} · {codice}
          <Badge variant="outline" className="ml-auto text-[10px]" data-testid="pod-timeline-count">
            {loading ? '...' : `${contracts.length} contratti`}
          </Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">Cronologia forniture su questo {label} per CF {codiceFiscale}</p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && <p className="text-xs text-slate-400 py-4 text-center">Caricamento...</p>}
        {!loading && contracts.length === 0 && (
          <p className="text-sm text-slate-500 py-3 text-center">Nessun altro contratto trovato su questo {label}.</p>
        )}
        {!loading && contracts.length > 0 && (
          <ol className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-2 space-y-4">
            {contracts.map(c => {
              const isCurrent = c.id === currentContractId;
              return (
                <li key={c.id} className="ml-4" data-testid={`pod-history-item-${c.id}`}>
                  <div className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white dark:border-slate-900 ${
                    c.stato === 'attiva' || c.stato === 'pagabile' ? 'bg-emerald-500' :
                    c.stato === 'chiuso' ? 'bg-slate-400' :
                    c.stato === 'scartata' || c.stato === 'annullata' ? 'bg-red-500' :
                    'bg-indigo-500'
                  }`} />
                  <div className={`rounded-lg border p-3 ${isCurrent ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200'}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {c.fornitore || '-'} · {c.offerta || 'Offerta'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          Pratica {c.codice_pratica || c.id.slice(0, 8)} · {c.sub_agente}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATO_COLOR[c.stato] || 'bg-slate-100 text-slate-700'}`}>
                          {c.stato_label}
                        </span>
                        {!isCurrent && (
                          <button
                            onClick={() => navigate(`/contracts/${c.id}`)}
                            className="p-1 rounded hover:bg-slate-100 text-slate-500"
                            title="Apri pratica"
                            data-testid={`pod-history-open-${c.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Inserita {formatDate(c.created_at)}</span>
                      {c.data_attivazione && <span>Attivata {formatDate(c.data_attivazione)}</span>}
                      {c.data_fine_effettiva && <span>Chiusa {formatDate(c.data_fine_effettiva)}</span>}
                      {isCurrent && <span className="font-semibold text-indigo-700">↑ Corrente</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default PodTimelineCard;
